import { useCallback, useState, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import { GenericDialog } from '@/components/shared/GenericDialog';
import {
  AllPointOfInterestTemplateDocument,
  TemplateTypeEnum,
  useAllPointOfInterestTemplateQuery,
  useAllTemplatesForBuilderQuery,
  useClonePointOfInterestTemplateMutation,
} from '@/__generated__/graphql';
import { AllPointOfInterestTemplate } from '@/types';
import { useSnackBarMessage } from './utils';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { tooltipStyles, AdditionalOptions, OptionChip } from './styles';
import { selectedTemplateId } from './state';
import { useAtomValue } from 'jotai';

type AddTemplateToStructureDialogProps = {
  structureId: string;
  isOpen: boolean;
  onClose: () => void;
  structureName: string;
  currentStructureId: string;
  existingTemplates: string[];
};

export const AddTemplateToStructureDialog = ({
  structureId,
  isOpen,
  onClose,
  structureName,
  currentStructureId,
  existingTemplates,
}: AddTemplateToStructureDialogProps) => {
  const destinationAssemblyTemplateId = useAtomValue(selectedTemplateId);
  const [assemblyTemplateId, setAssemblyTemplateId] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [allPoiTemplates, setAllPoiTemplates] = useState<AllPointOfInterestTemplate>([]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { data: allTemplatesData } = useAllTemplatesForBuilderQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (allTemplatesData?.allTemplates && allTemplatesData.allTemplates.length > 0) {
      setAssemblyTemplateId(allTemplatesData.allTemplates.at(0)?.id ?? '');
    }
  }, [allTemplatesData]);

  const { data: poiTemplatesData } = useAllPointOfInterestTemplateQuery({
    variables: {
      structureId,
      assemblyTemplateId,
    },
    fetchPolicy: 'cache-first',
    skip: !structureId || !assemblyTemplateId,
  });

  useEffect(() => {
    if (poiTemplatesData?.allPointOfInterestTemplate) {
      setAllPoiTemplates(poiTemplatesData?.allPointOfInterestTemplate);
    }
  }, [poiTemplatesData?.allPointOfInterestTemplate]);

  const handleChange = useCallback((value: string[]) => {
    setSelectedOptions(value);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedOptions([]);
    onClose();
  }, [onClose]);

  const [clonePointOfInterestTemplateMutation, { loading }] =
    useClonePointOfInterestTemplateMutation();

  const poiIds = useMemo(() => {
    return allPoiTemplates
      .filter((template) => selectedOptions.includes(template.name))
      .map((template) => template.id);
  }, [allPoiTemplates, selectedOptions]);

  const addTemplatesToStructure = useCallback(async () => {
    if (!destinationAssemblyTemplateId) return;

    await clonePointOfInterestTemplateMutation({
      variables: {
        input: {
          assemblyTemplateId: destinationAssemblyTemplateId,
          pointOfInterestTemplateIds: poiIds,
          structureId: currentStructureId,
          lastModifiedFor: 'Clone POI Template',
        },
      },
      refetchQueries: [AllPointOfInterestTemplateDocument],
      onCompleted: () => {
        onClose();
        showSnackBar(
          `${selectedOptions.join(
            ', '
          )} template(s) have been cloned for ${structureName} successfully`,
          'success'
        );
      },
      onError: (error) => {
        console.error(error);
        onClose();
        showSnackBar(`${error.message}`, 'error');
      },
    });
  }, [
    clonePointOfInterestTemplateMutation,
    poiIds,
    currentStructureId,
    onClose,
    showSnackBar,
    selectedOptions,
    structureName,
    destinationAssemblyTemplateId,
  ]);

  return (
    <GenericDialog
      open={isOpen}
      onClose={handleClose}
      showCloseIcon
      title={`Select templates to add to ${structureName}`}
      content={
        <FormControl fullWidth>
          <Autocomplete
            fullWidth
            multiple
            disableCloseOnSelect
            options={allPoiTemplates.map((template) => template.name)}
            value={selectedOptions}
            onChange={(event, newValue) => {
              if (newValue.some((option) => existingTemplates.includes(option.toLowerCase()))) {
                return;
              }
              handleChange(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Select Templates"
                placeholder="Select templates"
                fullWidth
              />
            )}
            getOptionLabel={(option) => option ?? ''}
            renderOption={(props, option, { selected }) => {
              const existingTemplatesSet = new Set(
                existingTemplates.map((template) => template.toLowerCase())
              );
              const isDisabled = existingTemplatesSet.has(option.toLowerCase());

              return (
                <li {...props}>
                  {isDisabled ? (
                    <Tooltip
                      title={`${option} template already exists for ${structureName}`}
                      arrow
                      disableInteractive={false}
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: isDisabled ? '#9e9e9e' : 'inherit',
                          opacity: isDisabled ? 0.6 : 1,
                        }}
                      >
                        <Checkbox
                          checked={selected}
                          disabled={isDisabled}
                          style={{ marginRight: 8 }}
                        />
                        <ListItemText primary={option} />
                      </span>
                    </Tooltip>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={selected}
                        disabled={isDisabled}
                        style={{ marginRight: 8 }}
                      />
                      <ListItemText primary={option} />
                    </span>
                  )}
                </li>
              );
            }}
            renderTags={(value, getTagProps) => {
              const chipLimit = 2;
              const additionalValues =
                value.length > chipLimit ? value.slice(chipLimit).join(', ') : '';
              return (
                <>
                  {value.slice(0, chipLimit).map((option, index) => (
                    <OptionChip {...getTagProps({ index })} label={option} key={option} />
                  ))}
                  <Tooltip title={additionalValues} arrow placement="bottom" sx={tooltipStyles}>
                    <AdditionalOptions>
                      {value.length > chipLimit && `+ ${value.length - chipLimit}`}
                    </AdditionalOptions>
                  </Tooltip>
                </>
              );
            }}
          />
        </FormControl>
      }
      actions={
        <>
          <Button onClick={handleClose} variant="text" sx={textButtonStyle}>
            Cancel
          </Button>
          <Button
            onClick={addTemplatesToStructure}
            sx={containedButtonStyle}
            disabled={loading}
            variant="contained"
          >
            {loading ? 'Copying' : 'Confirm'}
            {loading && <CircularProgress color="info" size={15} sx={{ ml: 1 }} />}
          </Button>
        </>
      }
      actionsStyles={{ mr: 3, pb: 3 }}
    />
  );
};
